import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { preprocessMath } from "../utils/mathPreprocess";
import { getAuthHeaders } from "../utils/auth";
import { useLanguage } from "../utils/i18n";

export default function ChatTab({ book, api, onAddNote, messages: messagesProp, setMessages: setMessagesProp }) {
  const [localMessages, setLocalMessages] = useState([]);
  const messages = messagesProp !== undefined ? messagesProp : localMessages;
  const setMessages = setMessagesProp !== undefined ? setMessagesProp : setLocalMessages;
  const [input, setInput] = useState("");
  // attachments: [{id, name, type:'image'|'doc', base64, text, preview, loading, error}]
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef();
  const fileRef = useRef();
  const { t } = useLanguage();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addImageAttachment = (file) => {
    const id = `${Date.now()}-${Math.random()}`;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setAttachments((prev) => [
        ...prev,
        { id, name: file.name, type: "image", base64, preview: reader.result, loading: false },
      ]);
    };
    reader.readAsDataURL(file);
  };

  const addDocAttachment = async (file) => {
    const id = `${Date.now()}-${Math.random()}`;
    setAttachments((prev) => [...prev, { id, name: file.name, type: "doc", loading: true }]);
    try {
      const authHeaders = await getAuthHeaders();
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${api}/extract-text`, { method: "POST", headers: authHeaders, body: formData });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setAttachments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, loading: false, text: data.text } : a))
      );
    } catch (err) {
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === id ? { ...a, loading: false, error: err.message || "Could not read file" } : a
        )
      );
    }
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (file.type.startsWith("image/")) addImageAttachment(file);
      else addDocAttachment(file);
    }
    e.target.value = "";
  };

  const removeAttachment = (id) =>
    setAttachments((prev) => prev.filter((a) => a.id !== id));

  // Paste image directly from clipboard (Ctrl+V in textarea)
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) addImageAttachment(file);
        break;
      }
    }
  };

  const send = async () => {
    const text = input.trim();
    const readyAttachments = attachments.filter((a) => !a.loading && !a.error);
    if (!text && readyAttachments.length === 0) return;

    const imageAttachments = readyAttachments.filter((a) => a.type === "image");
    const docAttachments = readyAttachments.filter((a) => a.type === "doc" && a.text);
    const doc_context = docAttachments
      .map((a) => `[${a.name}]\n${a.text}`)
      .join("\n\n---\n\n");

    const userMsg = {
      role: "user",
      content: text,
      previews: imageAttachments.map((a) => a.preview),
      docNames: docAttachments.map((a) => a.name),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setAttachments([]);
    setLoading(true);

    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${api}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          messages: [
            ...messages.filter((m) => m.role !== "system"),
            { role: "user", content: text || "(see attached files)" },
          ],
          book_id: book?.id || null,
          images: imageAttachments.map((a) => a.base64),
          doc_context: doc_context || null,
        }),
      });
      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply || data.error || "No response" },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: t("chatError") },
      ]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !loading) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <div className="chat-messages">
        {messages.length === 0 && (
          <div style={{ textAlign: "center", color: "#94a3b8", padding: "40px 20px", fontSize: "0.9rem" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>💬</div>
            <p>{t("chatEmptyTitle")}</p>
            <p style={{ marginTop: "6px", fontSize: "0.8rem" }}>
              {t("chatEmptyHint")}
            </p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`chat-message ${msg.role}`}>
            {msg.previews?.map((src, j) => (
              <img key={j} src={src} alt="uploaded" style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 6, marginBottom: 4, display: "block" }} />
            ))}
            {msg.docNames?.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                {msg.docNames.map((n, j) => (
                  <span key={j} style={{ background: "#e0e7ff", borderRadius: 6, padding: "2px 8px", fontSize: "0.78rem", color: "#3730a3" }}>📄 {n}</span>
                ))}
              </div>
            )}
            {msg.role === "assistant" ? (
              <div className="markdown-content">
                <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                  {preprocessMath(msg.content)}
                </ReactMarkdown>
                {onAddNote && (
                  <button
                    className="add-note-btn"
                    style={{ marginTop: "8px" }}
                    onClick={() => onAddNote(`Chat – ${new Date().toLocaleTimeString()}`, msg.content)}
                  >
                    {t("addToNotes")}
                  </button>
                )}
              </div>
            ) : (
              msg.content
            )}
          </div>
        ))}
        {loading && (
          <div className="chat-message assistant">
            <div className="loading-dots">
              <span /><span /><span />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="chat-input-area">
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf,.docx,.doc,.xlsx,.xls,.csv,.pptx,.ppt"
          multiple
          hidden
          onChange={handleFilesSelected}
        />

        <button
          className="icon-btn"
          onClick={() => fileRef.current.click()}
          title="Attach files — images, PDF, Word, Excel, CSV, PowerPoint"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </button>

        <div className="chat-input-wrapper">
          {attachments.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "6px 0 4px" }}>
              {attachments.map((a) => (
                <div
                  key={a.id}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: a.loading ? "#f1f5f9" : a.error ? "#fee2e2" : a.type === "image" ? "#f0fdf4" : "#e0e7ff",
                    border: `1px solid ${a.loading ? "#e2e8f0" : a.error ? "#fca5a5" : a.type === "image" ? "#86efac" : "#a5b4fc"}`,
                    borderRadius: 8, padding: "3px 8px", fontSize: "0.78rem",
                    color: a.error ? "#dc2626" : a.type === "image" ? "#166534" : "#3730a3",
                    maxWidth: 200,
                  }}
                >
                  {a.type === "image" && a.preview ? (
                    <img src={a.preview} alt="" style={{ width: 18, height: 18, objectFit: "cover", borderRadius: 3 }} />
                  ) : (
                    <span>{a.loading ? "⏳" : a.error ? "⚠️" : "📄"}</span>
                  )}
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.error ? `${a.name}: ${a.error}` : a.name}
                  </span>
                  {!a.loading && (
                    <button
                      onClick={() => removeAttachment(a.id)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontSize: "1rem", lineHeight: 1, padding: "0 2px", flexShrink: 0 }}
                    >×</button>
                  )}
                </div>
              ))}
            </div>
          )}
          <textarea
            className="chat-input"
            rows={1}
            placeholder={t("chatInputPlaceholder")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            onPaste={handlePaste}
          />
        </div>

        <button
          className="icon-btn"
          onClick={() => setMessages([])}
          disabled={messages.length === 0}
          title={t("clearChat")}
          style={{ opacity: messages.length === 0 ? 0.3 : 1, cursor: messages.length === 0 ? "default" : "pointer" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </button>

        <button
          className="send-btn"
          onClick={send}
          disabled={loading || (!input.trim() && !attachments.some((a) => !a.loading && !a.error))}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </>
  );
}
